import { ComboboxMultiple, type ComboboxMultipleProps } from '@eye-able/ui';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { availableAuditEntriesAtom } from '@/global-store';
import { getShortUrl } from '@helpers/shorten-url';
import { useLimitedComboboxItems } from '@hooks/useLimitedComboboxItems';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

export type DomainOption = {
  id: number;
  value: string;
  label: string;
  /** Full domain URL, shown under the alias in the option list. */
  domain: string;
  domainAlias?: string;
  /** Matched by the combobox search so a domain is found by alias or URL. */
  searchText?: string;
};

/**
 * The domains the current user can pick from, sourced from the global store and
 * shaped into combobox options. Centralized here so consumers don't each repeat
 * the atom read and mapping.
 */
export function useDomainOptions(): DomainOption[] {
  const [availableAuditEntries] = useAtom(availableAuditEntriesAtom);
  return useMemo(
    () =>
      availableAuditEntries.map((entry) => ({
        id: entry.id,
        value: String(entry.id),
        label: entry.domainAlias || entry.domain,
        domain: entry.domain,
        domainAlias: entry.domainAlias || undefined,
        searchText: entry.domainAlias
          ? `${entry.domainAlias} ${entry.domain}`
          : entry.domain,
      })),
    [availableAuditEntries],
  );
}

interface FormDomainSelectProps {
  /** Selected domain ids. */
  value: number[];
  onValueChange: (ids: number[]) => void;
  /**
   * Domains to choose from. Defaults to every domain the user has access to, so
   * most callers can omit it.
   */
  options?: DomainOption[];
  /** Field label. */
  label?: string;
  /** Trigger placeholder shown when nothing is selected. */
  placeholder?: string;
  /** Marks the field as invalid (e.g. when no domain is selected on submit). */
  invalid?: boolean;
  /**
   * Slot overrides forwarded to the underlying `ComboboxMultiple`, e.g. to
   * style the label or trigger. Merged with the component's own slot props;
   * the invalid state always wins on the trigger.
   */
  slotProps?: ComboboxMultipleProps<DomainOption>['slotProps'];
}

/**
 * Reusable multi-select for domains. It fetches the available domains itself
 * (override via `options`) and works in domain ids, so callers don't manage the
 * option list or resolve ids into option objects.
 *
 * Accounts can have thousands of domains, so rendering the full option list
 * lags the combobox. This caps the rendered options via `useLimitedComboboxItems`
 * (type-ahead past the threshold), the same approach the report `DomainSelect`
 * uses.
 */
export function FormDomainSelect({
  value,
  onValueChange,
  options,
  label,
  placeholder,
  invalid,
  slotProps,
}: FormDomainSelectProps) {
  const { t } = useInlineTranslation();
  const availableOptions = useDomainOptions();
  const resolvedOptions = options ?? availableOptions;
  const [searchValue, setSearchValue] = useState('');

  const selectedIds = useMemo(() => new Set(value), [value]);
  // Filtered from `resolvedOptions` so the selected items share their object
  // references — that's how ComboboxMultiple matches selection by default.
  const selectedOptions = useMemo(
    () => resolvedOptions.filter((option) => selectedIds.has(option.id)),
    [resolvedOptions, selectedIds],
  );
  const selectedValues = useMemo(
    () => selectedOptions.map((option) => option.value),
    [selectedOptions],
  );

  const { comboboxItems } = useLimitedComboboxItems({
    items: resolvedOptions,
    searchValue,
    selectedValues,
  });

  return (
    <ComboboxMultiple
      label={label}
      items={resolvedOptions}
      filteredItems={comboboxItems}
      inputValue={searchValue}
      onInputValueChange={setSearchValue}
      value={selectedOptions}
      onValueChange={(next) => onValueChange(next.map((option) => option.id))}
      itemToStringLabel={(item) => item.label}
      itemToStringValue={(item) => item.value}
      renderChipLabel={(item) => item.label}
      renderItemLabel={(item) =>
        item.domainAlias ? (
          <div className='eye:min-w-0 eye:break-all eye:line-clamp-3'>
            <div>{item.domainAlias}</div>
            <div className='eye:text-12'>{getShortUrl(item.domain, 60)}</div>
          </div>
        ) : (
          item.label
        )
      }
      slotProps={{
        ...slotProps,
        trigger: {
          showClear: true,
          ...slotProps?.trigger,
          'aria-invalid': invalid || undefined,
        },
        value: {
          placeholder,
          ...slotProps?.value,
        },
        searchInput: {
          placeholder: t('searchPlaceholder'),
          ...slotProps?.searchInput,
        },
      }}
    />
  );
}
